import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { extractErrorMessage } from '@/queries/ticketMutations'
import { useTrackerRefPrefixes, useSetTrackerRefPrefix } from '@/queries/settings'
import type { TrackerRefPrefixRow } from '@/queries/settings'

const PREFIX_PATTERN = /^[A-Z]{2,4}$/

function PrefixRow({ row }: { row: TrackerRefPrefixRow }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(row.refPrefix)
  const [error, setError] = useState<string | null>(null)
  const setPrefix = useSetTrackerRefPrefix()

  function startEditing() {
    setValue(row.refPrefix)
    setError(null)
    setEditing(true)
  }

  async function save() {
    const next = value.trim().toUpperCase()
    if (!PREFIX_PATTERN.test(next)) {
      setError('Must be 2-4 uppercase letters')
      return
    }
    try {
      await setPrefix.mutateAsync({ companyId: row.companyId, refPrefix: next })
      setEditing(false)
      setError(null)
    } catch (err) {
      // 409 (already used by another company) and 400 both land here —
      // surfaced inline on the row rather than a toast, so it's clear which
      // company's edit failed.
      setError(extractErrorMessage(err, 'Could not save'))
    }
  }

  return (
    <tr className="border-b border-border last:border-0">
      <td className="py-2 pr-4 text-sm text-foreground">{row.name}</td>
      <td className="py-2 pr-4">
        {editing ? (
          <div className="flex items-center gap-2">
            <Input
              aria-label={`${row.name} ref prefix`}
              value={value}
              onChange={e => setValue(e.target.value.toUpperCase())}
              maxLength={4}
              className="h-7 w-20 text-xs uppercase"
              autoFocus
            />
            <Button size="sm" className="h-7" onClick={save} disabled={setPrefix.isPending}>
              {setPrefix.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditing(false)} disabled={setPrefix.isPending}>
              Cancel
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={startEditing}
            className="rounded border border-transparent px-1.5 py-0.5 text-xs font-mono text-foreground hover:border-border hover:bg-muted"
          >
            {row.refPrefix}
            {!row.isCustom && <span className="ml-1.5 text-muted-foreground">(default)</span>}
          </button>
        )}
        {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
      </td>
    </tr>
  )
}

// Admin settings surface for per-company ticket-ref prefixes (design doc
// "Ticket references", 2026-08-24) — lists every company that has at least
// one ticket (a prefix is only meaningful once there's something to number)
// and lets an admin override one, edit-in-place. Changing a prefix affects
// only tickets minted after the change; existing refs are untouched.
export function TicketRefPrefixesView() {
  const { data, isLoading, error } = useTrackerRefPrefixes()

  return (
    <div className="container mx-auto max-w-4xl px-6 pb-6">
      <Card>
        <CardHeader>
          <CardTitle>Ticket Reference Prefixes</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {error && <p className="text-sm text-destructive">{extractErrorMessage(error, 'Could not load prefixes')}</p>}
          {data && data.data.length === 0 && (
            <p className="text-sm text-muted-foreground">No companies have tickets yet.</p>
          )}
          {data && data.data.length > 0 && (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-border text-xs text-muted-foreground">
                  <th className="pb-2 pr-4 font-normal">Company</th>
                  <th className="pb-2 pr-4 font-normal">Prefix</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map(row => (
                  <PrefixRow key={row.companyId} row={row} />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
